import { useEffect, useState, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Briefcase, Inbox } from "lucide-react";
import { Card, SegmentedControl } from "@/components/my-activity/layout";
import { RequestsSection } from "@/components/my-activity/requests-section";
import { ScopePanel, WorkSection } from "@/components/my-activity/work-section";
import { Feature, useSession } from "@/components/session-provider";
import { ApiError } from "@/lib/api";
import {
  DEFAULT_MY_ACTIVITY_RANGE,
  DEFAULT_MY_ACTIVITY_RELATIONSHIP,
  MY_ACTIVITY_RANGES,
  MY_ACTIVITY_RELATIONSHIPS,
  fetchMyActivity,
  relationshipBlurb,
  relationshipLabel,
  type MyActivity,
  type MyActivityRangeDays,
  type MyActivitySet,
} from "@/lib/my-activity";

interface MyActivitySearch {
  days?: MyActivityRangeDays;
  set?: MyActivitySet;
}

/**
 * ⚠️ Every value validated against a known set and dropped if it does not match, the same rule
 * the queue and the analytics screen follow. The server REFUSES an unlisted range and an
 * unreadable set rather than clamping either (D-60), so a mangled link has to resolve to the
 * default here rather than travel and come back a 400 the screen would have to render.
 *
 * ⚠️ `set` rather than `relationship`, because it is typed into a URL bar and read aloud.
 * Nothing else on this route uses the name, so there is no collision to inherit.
 *
 * ⚠️ **`lens` is gone and is deliberately not tolerated as a leftover.** D-171 shipped two
 * selectable views and D-172 merged them, so a `?lens=` in a bookmark or a pasted link now names
 * nothing. It is dropped like any other unknown value and the page renders whole — which is the
 * right answer, because both halves are on it either way and there is nothing the parameter
 * could have selected.
 */
function validateSearch(search: Record<string, unknown>): MyActivitySearch {
  const result: MyActivitySearch = {};

  const days = Number(search.days);
  if (MY_ACTIVITY_RANGES.includes(days as MyActivityRangeDays)) {
    result.days = days as MyActivityRangeDays;
  }

  if (MY_ACTIVITY_RELATIONSHIPS.includes(search.set as MyActivitySet)) {
    result.set = search.set as MyActivitySet;
  }

  return result;
}

export const Route = createFileRoute("/my-activity")({
  // In the URL so a reader can bookmark the view they use, come back to it with the browser's
  // own back button, and land on the same figures — none of which works from component state.
  validateSearch,
  component: MyActivityPage,
});

/**
 * My activity — one page: an agent's own work, and their own requests.
 *
 * ⚠️ <b>ONE PAGE, NOT TWO TABS, AND THAT IS D-172.</b> D-171 shipped these as two selectable
 * lenses, on the argument that a payload carrying both was one edit away from a work figure drawn
 * under a requester heading. Agents asked for one page and they are right: an agent switching
 * views to answer "what is late" and then switching back to answer "did anyone reply to my own
 * request" is two clicks and a lost scroll position for two facts that fit on one screen.
 *
 * ⚠️ <b>So the property that made the split safe had to be replaced rather than dropped.</b> It
 * is now that the two halves are different TYPES rendered by different components — the sections
 * cannot be crossed by accident, only by somebody deliberately passing the wrong prop, which does
 * not compile. That is weaker than "the other half does not exist", and every card on both halves
 * therefore names its own frame on screen.
 *
 * ⚠️ <b>The order is the priority.</b> Work first for anybody who has it: an agent opens this to
 * find out what is on their plate, not what they asked IT for last quarter. Their own requests
 * follow, in full and unchanged. What their access covers is last, because it is reference rather
 * than a thing to act on.
 *
 * ⚠️ <b>Nothing here names a colleague and nothing ranks anybody.</b> No other agent, no team
 * average, no percentile, no target, no benchmark, no score. Asserted server-side by walking the
 * response.
 */
function MyActivityPage() {
  const { days, set } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user, hasFeature } = useSession();

  // ⚠️ The set selector is drawn only where the relationship exists, from the session's own flag
  // rather than a second query (D-47). Somebody never invited onto a colleague's request has one
  // possible answer, and a control with one option is furniture that needs explaining (D-116).
  const canChooseSet = user?.ticketTabs.hasSharedWithMe ?? false;

  const range = days ?? DEFAULT_MY_ACTIVITY_RANGE;

  // ⚠️ THE DEFAULT ADAPTS AND AN EXPLICIT CHOICE ALWAYS WINS, and both halves matter.
  //
  // Defaulting everybody to "both" would put the sentence "a count here can include a
  // colleague's request" above figures that, for anybody never invited onto one, cannot — a
  // heading describing a set the reader does not have. So the default is "mine" for them.
  //
  // ⚠️ And a `?set=` in the URL is honoured even when the selector is hidden: forcing it yields
  // an empty page, which is the truth about their own data, rather than silently different
  // figures from the ones the URL names. That is D-47's rule for the queue's own tabs.
  const relationship: MyActivitySet =
    set ?? (canChooseSet ? DEFAULT_MY_ACTIVITY_RELATIONSHIP : "Mine");

  const state = useMyActivity(range, relationship);

  if (!hasFeature(Feature.MyActivity)) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <h1 className="text-xl font-semibold tracking-tight">My activity</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This page is switched off for your organisation.
        </p>
      </div>
    );
  }

  const data = state.data;

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">My activity</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {/* ⚠️ The subject of the page, not a promise about a section that may not be drawn.
                Until the first response arrives nobody knows whether this reader works requests,
                and guessing from the permission list is the defect D-51 and D-136 both found —
                permissions reach the browser without scope. */}
            {data?.work
              ? "Your service desk work, and the requests you have raised yourself."
              : "What you have asked the service desk for."}
          </p>
        </div>

        {/* ⚠️ The range applies to BOTH halves, which is why it lives up here rather than in a
            section header — and why each half says which frame it counts in. Ninety days of
            "raised" and ninety days of "finished" are different sets of the same width. */}
        <SegmentedControl
          label="Over"
          options={MY_ACTIVITY_RANGES.map((value) => ({
            value: String(value),
            label: value === 365 ? "1 year" : `${value} days`,
          }))}
          value={String(range)}
          onChange={(value) =>
            void navigate({
              search: (prev: MyActivitySearch) => ({
                ...prev,
                days: Number(value) as MyActivityRangeDays,
              }),
              replace: true,
            })
          }
        />
      </header>

      {/* ⚠️ A failure renders AS a failure, never as an empty screen. "You have never raised a
          request" and "this did not load" are different facts about somebody's own history, and
          collapsing them into one rendering is the defect D-122 found on the bulk picker — in the
          direction that invites somebody to believe something untrue about their own data. The
          server's own sentence is surfaced where it sent one: a 400 refusing a range names the
          ranges that exist, and replacing that with "something went wrong" throws away the only
          line telling the reader what to do. */}
      {state.error !== null ? (
        <Card>
          <p className="py-6 text-center text-sm text-muted-foreground">{state.error}</p>
        </Card>
      ) : state.loading || !data ? (
        <Skeleton />
      ) : (
        <div className="space-y-10">
          {/* ⚠️ Drawn only where the server sent it, and its absence is the ONLY signal — there
              is no companion boolean that could come to disagree with the data beside it (D-122,
              D-136). Withheld from the wire for anybody who is not the desk (D-82), so there is
              nothing here to hide in React. */}
          {data.work && (
            <Section
              icon={Briefcase}
              title="Your work"
              blurb="Requests assigned to you. Counted by when you finished them, and never compared to anybody else."
            >
              <WorkSection data={{ ...data, work: data.work }} />
            </Section>
          )}

          <Section
            icon={Inbox}
            title={data.work ? "Your own requests" : "Your requests"}
            blurb={relationshipBlurb[relationship]}
            action={
              // ⚠️ IN THIS SECTION'S HEADER RATHER THAN THE PAGE'S, and that placement is the
              // decision. It narrows this half and nothing else; at the top of the page, beside
              // the range, it would read as narrowing the work figures too — which are pinned to
              // requests assigned to the reader and cannot be narrowed by it at all.
              canChooseSet ? (
                <SegmentedControl
                  label="Which requests"
                  options={MY_ACTIVITY_RELATIONSHIPS.map((value) => ({
                    value,
                    label: relationshipLabel[value],
                  }))}
                  value={relationship}
                  onChange={(value) =>
                    void navigate({
                      search: (prev: MyActivitySearch) => ({
                        ...prev,
                        set: value as MyActivitySet,
                      }),
                      replace: true,
                    })
                  }
                />
              ) : undefined
            }
          >
            <RequestsSection data={data} requests={data.requests} />
          </Section>

          {/* ⚠️ Last, and outside both sections, because it is reference rather than a thing to
              act on — and because it describes the reader's ACCESS, which is neither half's
              subject. Inside the work section it sat in the middle of the page, between the two
              things somebody came to read. */}
          {data.work && <ScopePanel scope={data.work.scope} />}
        </div>
      )}
    </div>
  );
}

/**
 * One half of the page, under a heading that says what it is about.
 *
 * ⚠️ <b>THE HEADING AND THE BLURB ARE PART OF THE CONTROL, not decoration.</b> The two halves
 * count in different frames — raised (D-53) above resolved (D-55) — and on one page they are a
 * scroll apart rather than behind a control that named which was on screen. Copy that describes a
 * control is part of it (D-114, D-137), and the failure here is silent: every figure stays
 * plausible, somebody just reads one against the other.
 *
 * ⚠️ A rule and generous spacing rather than a card wrapper, because these already contain cards
 * and a box inside a box reads as a nesting nobody meant.
 */
function Section({
  icon: Icon,
  title,
  blurb,
  action,
  children,
}: {
  icon: (props: { className?: string; "aria-hidden"?: boolean }) => ReactNode;
  title: string;
  blurb: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-label={title}>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10">
            <Icon className="h-4 w-4 text-primary" aria-hidden />
          </span>

          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight">{title}</h2>
            <p className="mt-0.5 text-xs text-muted-foreground">{blurb}</p>
          </div>
        </div>

        {action && <div className="shrink-0">{action}</div>}
      </header>

      {children}
    </section>
  );
}

interface MyActivityState {
  data: MyActivity | null;
  error: string | null;
  loading: boolean;
}

/**
 * The screen's figures, fetched on the reader's own choices.
 *
 * ⚠️ `data` is cleared the moment the range or the set changes, on `useAnalyticsSection`'s own
 * reasoning: leaving the previous figures on screen beside a fresh label is the one failure this
 * kind of page cannot afford, because stale numbers look exactly like current ones and nothing
 * about a chart says it is answering last month's question.
 */
function useMyActivity(days: MyActivityRangeDays, relationship: MyActivitySet): MyActivityState {
  const [state, setState] = useState<MyActivityState>({
    data: null,
    error: null,
    loading: true,
  });

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    setState({ data: null, error: null, loading: true });

    void (async () => {
      try {
        const result = await fetchMyActivity({ days, relationship }, controller.signal);
        if (!cancelled) setState({ data: result, error: null, loading: false });
      } catch (e) {
        if (cancelled || controller.signal.aborted) return;

        setState({
          data: null,
          error:
            e instanceof ApiError
              ? e.message
              : "We could not load your activity just now. Try again in a moment.",
          loading: false,
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [days, relationship]);

  return state;
}

function Skeleton() {
  return (
    <div className="space-y-6" aria-busy="true" aria-label="Loading your activity">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((n) => (
          <div key={n} className="h-[86px] animate-pulse rounded-xl border border-border bg-card" />
        ))}
      </div>
      <div className="h-72 animate-pulse rounded-2xl border border-border bg-card" />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="h-56 animate-pulse rounded-2xl border border-border bg-card" />
        <div className="h-56 animate-pulse rounded-2xl border border-border bg-card" />
      </div>
    </div>
  );
}
